/**
 * @owner @Appboy/agent-console
 */

import { rulesetContextCopy } from "./rulesetContextCopy";
import { presetContextsFor, presetDefinitionFor, presetTypesFor } from "./rulesetPresets";
import { PRESET_RULESET_TYPES, presetAgentTypes, RULESET_SCOPES } from "./types";
import type { PresetRulesetType, QaPresetAgentType, RulesetContext, RulesetScope } from "./types";

/** The summary a ruleset is evaluated against. */
interface EvaluatedSummary {
  /** The summary's noun, for context descriptions that enrich it. */
  noun: string;
  /** The context description for a preset that unlocks nothing beyond the summary. */
  baseContext: string;
}

const campaignSummary: EvaluatedSummary = {
  noun: "campaign summary",
  baseContext: "Base campaign summary only",
};

// Canvas QA summaries by ruleset scope. The Audience Paths sections are listed
// in the order CanvasSummaryBuilder emits them.
const canvasSummaries: Record<RulesetScope, EvaluatedSummary> = {
  [RULESET_SCOPES.CANVAS]: { noun: "Canvas summary", baseContext: "Base Canvas summary only" },
  [RULESET_SCOPES.MESSAGE_STEP]: {
    noun: "message step summary",
    baseContext: "Message step summary with Canvas context only",
  },
  [RULESET_SCOPES.AUDIENCE_PATHS_STEP]: {
    noun: "Audience Paths step summary",
    baseContext:
      "Audience Paths step summary with Canvas context (delivery, frequency capping, re-entry, audience, conversion events, exit criteria, structure with each decision split's criteria, other settings)",
  },
};

/** The contexts a preset unlocks for another Standard kind but not this one. */
const absentContextsFor = (type: PresetRulesetType, agentType: QaPresetAgentType): RulesetContext[] => {
  const received = presetContextsFor(type, agentType);
  const receivedElsewhere = (Object.values(presetAgentTypes) as QaPresetAgentType[])
    .filter((other) => other !== agentType)
    .flatMap((other) => presetContextsFor(type, other));
  return [...new Set(receivedElsewhere)].filter((context) => !received.includes(context));
};

/**
 * What kinds of rules belong in each preset ruleset category, for the purpose of
 * teaching BrazeAI Operator how to categorize rules. Which agent types offer a
 * preset, the scope it is offered under and the context it unlocks all come from
 * the shared `RulesetPresets` table (see `rulesetPresets.ts`); only the prose
 * lives here.
 *
 * Typed as `Record<PresetRulesetType, ...>` so a new preset type added to
 * `Braze::Agentic::Ruleset::PresetTypes` (which flows into `PresetRulesetType`
 * via shared_constants) is a compile error here until its prose is written — so
 * the Operator tool descriptions and instruction tables built from this map can
 * never silently omit it.
 */
export const PRESET_RULESET_METADATA: Record<PresetRulesetType, string> = {
  [PRESET_RULESET_TYPES.CAMPAIGN_SETUP]: "Naming, tags, schedule/timing, conversion events",
  [PRESET_RULESET_TYPES.AUDIENCE_AND_TARGETING]:
    "Target segments, exclusions/suppressions, audience size, filter logic",
  [PRESET_RULESET_TYPES.CONTENT_AND_COPY]:
    "Subject/preheader, sender name/reply-to, copy quality, spelling/grammar, character limits, brand voice/tone, cultural/historical sensitivity",
  [PRESET_RULESET_TYPES.LINKS_AND_TRACKING]:
    "CTAs/URLs, UTM parameters, malformed links, deep links, link destinations",
  [PRESET_RULESET_TYPES.PERSONALIZATION_AND_DYNAMIC_CONTENT]:
    "Liquid syntax, fallback/default values, conditional logic, content block references, repeated Liquid factored into variables (in Canvas, also Canvas context variables)",
  [PRESET_RULESET_TYPES.COMPLIANCE_AND_DELIVERABILITY]:
    "Unsubscribe/preference center, legal disclaimers, transactional separation, spam/deliverability, accessibility",
  [PRESET_RULESET_TYPES.CANVAS_SETUP]:
    "Canvas-level naming, tags, entry schedule/timing, conversion events, exit criteria, re-entry settings",
  [PRESET_RULESET_TYPES.CANVAS_STRUCTURE]:
    "Canvas flow: step connectivity, branching/decision logic, delays, branches that end before any message is sent",
  [PRESET_RULESET_TYPES.AUDIENCE_PATHS_PATH_AND_PRIORITY]:
    "Audience Paths steps: path labels/purpose, names consistent with their criteria, Audience Paths for current user attributes and audience state, Intelligent Channel paths matching their message channel, narrow-before-broad ordering, priority when paths overlap, random-bucket grouping under rate limits, unreachable paths",
};

/**
 * [type identifier, "use for" prose] for the preset categories the agent type
 * offers, in the shared table's (menu) order — so, e.g., Campaign QA Operator
 * instructions never mention Canvas-only presets.
 */
export const presetRulesetEntries = (agentType: QaPresetAgentType): [PresetRulesetType, string][] =>
  presetTypesFor(agentType).map((type) => [type, PRESET_RULESET_METADATA[type]]);

/** Number of preset ruleset categories the agent type offers. */
export const presetRulesetCount = (agentType: QaPresetAgentType): number => presetTypesFor(agentType).length;

/**
 * The evaluation-context description for a preset as a Standard of the given
 * agent type evaluates it: the context the preset unlocks, described against
 * the summary its scope is evaluated on, or that bare summary when it unlocks
 * none.
 *
 * @throws when the agent type does not offer the preset, which has no answer.
 */
export const presetRulesetContextFor = (type: PresetRulesetType, agentType: QaPresetAgentType): string => {
  const definition = presetDefinitionFor(type, agentType);
  if (definition === undefined) {
    throw new Error(`${agentType} Standards do not offer the preset "${type}"`);
  }
  const summary = definition.scope === null ? campaignSummary : canvasSummaries[definition.scope];
  const received =
    definition.contexts.length === 0
      ? summary.baseContext
      : definition.contexts
          .map((context) => rulesetContextCopy[context].operatorDescription(summary.noun))
          .join("; ");
  const absences = absentContextsFor(type, agentType).flatMap((context) => {
    const note = rulesetContextCopy[context].absenceNote;
    return note === null ? [] : [note];
  });
  if (absences.length === 0) {
    return received;
  }
  return `${received} — ${absences.join("; ")}`;
};
